import React, { useState } from 'react';

/**
 * Password requirements mirroring the Supabase Auth password policy
 * (minimum length + at least one character from each class).
 * Keep in sync with Supabase Dashboard -> Authentication -> Passwords.
 */
export const PASSWORD_REQUIREMENTS: { label: string; test: (value: string) => boolean }[] = [
  { label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { label: 'An uppercase letter (A-Z)', test: (v) => /[A-Z]/.test(v) },
  { label: 'A lowercase letter (a-z)', test: (v) => /[a-z]/.test(v) },
  { label: 'A number (0-9)', test: (v) => /\d/.test(v) },
  { label: 'A special character (!@#$%^&* etc.)', test: (v) => /[!@#$%^&*()_+\-=\[\]{};':"|<>?,./`~]/.test(v) },
];

/**
 * Returns true when the password satisfies every requirement in the
 * Supabase password policy. Used to gate form submission.
 */
export const meetsPasswordRequirements = (password: string): boolean =>
  PASSWORD_REQUIREMENTS.every((req) => req.test(password));

interface PasswordInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isConfirm?: boolean;
  confirmValue?: string;
  required?: boolean;
}

/**
 * A password input component with inline requirements validation
 * Features:
 * - Live requirements checklist (✓/✗ per rule) visible on focus or input
 * - Password matching validation (when used as confirm password)
 *
 * @param value - The current password value
 * @param onChange - Callback when password changes
 * @param placeholder - Input placeholder text
 * @param isConfirm - Whether this is a confirm password input
 * @param confirmValue - The value to match against (for confirm password)
 * @param required - Whether the field is required
 */
export const PasswordInput: React.FC<PasswordInputProps> = ({
  value,
  onChange,
  placeholder = 'Password',
  isConfirm = false,
  confirmValue,
  required = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  // Check if passwords match when in confirm mode
  const passwordsMatch = isConfirm ? value === confirmValue : true;

  return (
    <div className="w-full">
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        className={`input w-full ${
          isConfirm && value && !passwordsMatch ? 'input-error' : ''
        }`}
        required={required}
      />

      {/* Live requirements checklist for the main password input */}
      {!isConfirm && (isFocused || value) && (
        <ul className="mt-2 space-y-0.5">
          {PASSWORD_REQUIREMENTS.map((req) => {
            const met = req.test(value);
            return (
              <li
                key={req.label}
                className={`text-xs flex items-center gap-1.5 ${
                  met ? 'text-success' : 'text-base-content/60'
                }`}
              >
                <span className="w-3 text-center">{met ? '✓' : '•'}</span>
                {req.label}
              </li>
            );
          })}
        </ul>
      )}

      {/* Show match indicator for confirm password */}
      {isConfirm && value && (
        <p className={`fieldset-label ${passwordsMatch ? 'text-success' : 'text-error'}`}>
          {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
        </p>
      )}
    </div>
  );
};
